import { useState, useEffect, useCallback, useRef } from 'react';
import authService from '../services/authService';

/**
 * React hook for tier-based authorization and limits
 * Provides tier information, feature access checks, and refresh capability
 */
export const useTier = () => {
    const [tierInfo, setTierInfo] = useState({
        tier: 'Free',
        balance: 0,
        balanceCents: 0,
        limits: {
            maxOutputTokens: 4096,
            inputCap: 12288,
            dailyTransactionLimit: 20,
            maxSavedConversations: 10,
            maxBAIQLScripts: null
        },
        canAccessChatStudio: false,
        canAccessChatMode: false,
        canSaveBAIQL: false,
        canExecuteBAIQL: false,
        dailyTransactionsUsed: 0,
        dailyTransactionLimit: 20,
        baiqlExecutionsRemaining: null,
        savedConversationCount: 0,
        savedBAIQLScriptCount: 0,
        maxSavedConversations: 10,
        maxBAIQLScripts: null,
        availableModels: []
    });
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const previousTierRef = useRef(null);
    const previousBalanceRef = useRef(null);
    const previousBaiqlQuotaRef = useRef(null);
    const [tierChangeNotification, setTierChangeNotification] = useState(null);

    const fetchTierInfo = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const headers = authService.getAuthHeaders();
            const response = await fetch('/api/auth/tier-info', {
                headers,
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch tier info: ${response.status}`);
            }
            
            const data = await response.json();
            const newTier = data.tier || 'Free';
            const newBalance = data.balance || 0;
            const newBaiqlQuota = data.baiqlExecutionsRemaining;
            
            // Debug log for Admin users
            if (newTier === 'Admin') {
                console.log('[Tier] Admin user detected - granting full access', {
                    tier: newTier,
                    canAccessChatStudio: data.canAccessChatStudio,
                    canAccessChatMode: data.canAccessChatMode
                });
            }
            
            // Detect tier changes and show notifications
            const prevTier = previousTierRef.current;
            const prevBalance = previousBalanceRef.current;
            const prevBaiqlQuota = previousBaiqlQuotaRef.current;
            
            if (prevTier !== null) { // Skip on initial load
                // Tier upgrade: Free → Pro
                if (prevTier === 'Free' && newTier === 'Pro') {
                    setTierChangeNotification({
                        type: 'success',
                        message: "Congratulations! You're now a Pro user with unlimited AI transactions and access to 11 models."
                    });
                }
                // Tier downgrade: Pro → Free
                else if (prevTier === 'Pro' && newTier === 'Free') {
                    setTierChangeNotification({
                        type: 'warning',
                        message: "Your balance has dropped below $1. You've been moved to Free tier. Top up $10 to restore Pro access."
                    });
                }
                
                // Pro tier BAIQL quota changes
                if (newTier === 'Pro' && prevTier === 'Pro') {
                    // Balance dropped below $10 - quota activated
                    if (prevBalance >= 10 && newBalance < 10 && prevBaiqlQuota === null && newBaiqlQuota !== null) {
                        setTierChangeNotification({
                            type: 'warning',
                            message: "Balance below $10. You have 10 BAIQL executions remaining. Top up to $10 for unlimited."
                        });
                    }
                    // Balance restored above $10 - quota removed
                    else if (prevBalance < 10 && newBalance >= 10 && prevBaiqlQuota !== null && newBaiqlQuota === null) {
                        setTierChangeNotification({
                            type: 'success',
                            message: "BAIQL unlimited executions restored"
                        });
                    }
                }
            }
            
            // Update refs
            previousTierRef.current = newTier;
            previousBalanceRef.current = newBalance;
            previousBaiqlQuotaRef.current = newBaiqlQuota;
            
            setTierInfo({
                tier: newTier,
                balance: newBalance,
                balanceCents: data.balanceCents || 0,
                limits: {
                    maxOutputTokens: data.limits?.maxOutputTokens || 4096,
                    inputCap: data.limits?.inputCap || null,
                    dailyTransactionLimit: data.limits?.dailyTransactionLimit || null,
                    maxSavedConversations: data.limits?.maxSavedConversations || null,
                    maxBAIQLScripts: data.limits?.maxBAIQLScripts || null
                },
                canAccessChatStudio: data.canAccessChatStudio || false,
                canAccessChatMode: data.canAccessChatMode || false,
                canSaveBAIQL: data.canSaveBAIQL || false,
                canExecuteBAIQL: data.canExecuteBAIQL || false,
                dailyTransactionsUsed: data.dailyTransactionsUsed || 0,
                dailyTransactionLimit: data.limits?.dailyTransactionLimit || null,
                baiqlExecutionsRemaining: newBaiqlQuota,
                savedConversationCount: data.savedConversationCount || 0,
                savedBAIQLScriptCount: data.savedBAIQLScriptCount || 0,
                maxSavedConversations: data.limits?.maxSavedConversations || null,
                maxBAIQLScripts: data.limits?.maxBAIQLScripts || null,
                availableModels: data.availableModels || []
            });
        } catch (err) {
            console.error('Error fetching tier info:', err);
            setError(err.message);
            // Set defaults on error
            setTierInfo(prev => ({ ...prev, tier: 'Free' }));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTierInfo();
        
        // Refresh tier info when balance changes (via storage event)
        const handleStorageChange = (e) => {
            if (e.key === 'balanceRefreshTick') {
                fetchTierInfo();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [fetchTierInfo]);

    /**
     * Check if user can access a specific feature
     * @param {string} feature - Feature name (e.g., 'CHAT_STUDIO', 'BAIQL_SAVE', 'BAIQL_EXECUTE', 'CHAT_MODE', 'OPTIMIZE', 'MULTI_SEND')
     * @returns {boolean}
     */
    const canAccessFeature = useCallback((feature) => {
        // Admin users have access to everything
        if (tierInfo.tier === 'Admin') {
            return true;
        }

        switch(feature) {
            case 'CHAT_STUDIO':
                return tierInfo.canAccessChatStudio;
            case 'CHAT_MODE':
                return tierInfo.canAccessChatMode;
            case 'BAIQL_SAVE':
                return tierInfo.canSaveBAIQL;
            case 'BAIQL_EXECUTE':
                if (!tierInfo.canExecuteBAIQL) return false;
                // For Pro tier, check if quota is exhausted
                if (tierInfo.tier === 'Pro' && tierInfo.baiqlExecutionsRemaining !== null && tierInfo.baiqlExecutionsRemaining <= 0) {
                    return false;
                }
                return true;
            case 'OPTIMIZE':
                // Only available for Pro+ tiers (not Free)
                return tierInfo.tier !== 'Free';
            case 'MULTI_SEND':
                // Only available for Pro+ tiers (not Free)
                return tierInfo.tier !== 'Free';
            default:
                return false;
        }
    }, [tierInfo]);

    /**
     * Get user-friendly message for feature access denial
     * @param {string} feature - Feature name
     * @returns {string} Error message
     */
    const getFeatureDenialMessage = useCallback((feature) => {
        // Admin users should never see denial messages, but if they do, show a generic message
        if (tierInfo.tier === 'Admin') {
            return 'This feature should be available for Admin users. Please refresh the page.';
        }

        switch(feature) {
            case 'CHAT_STUDIO':
                return 'Upgrade to Pro to use CHAT Studio';
            case 'CHAT_MODE':
                return 'Upgrade to Pro to access Chat Mode';
            case 'BAIQL_SAVE':
                return 'Add $10 to unlock BAIQL script saving and execution';
            case 'BAIQL_EXECUTE':
                if (tierInfo.tier === 'Free') {
                    return 'Add $10 to unlock BAIQL execution';
                }
                if (tierInfo.tier === 'Pro' && tierInfo.baiqlExecutionsRemaining !== null && tierInfo.baiqlExecutionsRemaining <= 0) {
                    return 'BAIQL execution quota exhausted. Add funds to continue.';
                }
                return 'BAIQL execution not available';
            case 'OPTIMIZE':
                return 'Upgrade to Pro to access the Optimize feature';
            case 'MULTI_SEND':
                return 'Upgrade to Pro to access Multi Send';
            default:
                return 'This feature is not available for your tier';
        }
    }, [tierInfo]);

    return { 
        tierInfo, 
        canAccessFeature,
        getFeatureDenialMessage,
        refreshTier: fetchTierInfo,
        loading,
        error,
        tierChangeNotification,
        clearTierChangeNotification: () => setTierChangeNotification(null)
    };
};

